import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import type { CreateFolderCommand, UpdateFolderCommand } from '@/types/folder';
import { toast } from 'sonner';
import { invalidateActivityLogDelayed } from '@/hooks/queries/useActivityLogQuery';
import { projectKeys } from '@/hooks/queries/useProjectsQuery';

export const folderKeys = {
  all: ['folders'] as const,
  byProject: (projectId: number) => [...folderKeys.all, 'project', projectId] as const,
  countByProject: (projectId: number) => [...folderKeys.all, 'project', projectId, 'count'] as const,
  contents: (folderId: number) => [...folderKeys.all, 'contents', folderId] as const,
};

/** Root-level folders for a project (parentFolderId === null). */
export const useProjectFoldersQuery = (projectId: number) => {
  return useQuery({
    queryKey: folderKeys.byProject(projectId),
    queryFn: async () => {
      const all = await apiClient.getFoldersByProject(projectId);
      return all.filter((f) => f.parentFolderId === null);
    },
    enabled: !!projectId,
    gcTime: 5 * 60 * 1000,
  });
};

/** All folders for a project (flat list, used for ancestor-chain computation). */
export const useAllProjectFoldersQuery = (projectId: number, enabled = true) => {
  return useQuery({
    queryKey: [...folderKeys.byProject(projectId), 'all'] as const,
    queryFn: () => apiClient.getFoldersByProject(projectId),
    enabled: !!projectId && enabled,
    gcTime: 5 * 60 * 1000,
    staleTime: 30 * 1000,
  });
};

/** Total count of all folders (including subfolders) for a project. */
export const useFolderCountQuery = (projectId: number) => {
  return useQuery({
    queryKey: folderKeys.countByProject(projectId),
    queryFn: () => apiClient.getFolderCountByProject(projectId),
    enabled: !!projectId,
    gcTime: 5 * 60 * 1000,
    staleTime: 30 * 1000,
  });
};

/** Contents (subfolders + files) of a folder — only fetches when `enabled` is true (lazy on expand). */
export const useFolderContentsQuery = (folderId: number, enabled: boolean) => {
  return useQuery({
    queryKey: folderKeys.contents(folderId),
    queryFn: () => apiClient.getFolderContents(folderId),
    enabled: !!folderId && enabled,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateFolderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: CreateFolderCommand) => apiClient.createFolder(command),
    onSuccess: (folder) => {
      // Invalidate root list
      queryClient.invalidateQueries({ queryKey: folderKeys.byProject(folder.projectId) });
      // Invalidate total folder count
      queryClient.invalidateQueries({ queryKey: folderKeys.countByProject(folder.projectId) });
      // Invalidate the parent folder's contents cache so the new child appears
      if (folder.parentFolderId !== null && folder.parentFolderId !== undefined) {
        queryClient.invalidateQueries({ queryKey: folderKeys.contents(folder.parentFolderId) });
      }
      invalidateActivityLogDelayed(queryClient, folder.projectId);
      toast.success('Folder created');
    },
    onError: () => {
      toast.error('Failed to create folder');
    },
  });
};

export const useUpdateFolderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, command }: { id: number; command: UpdateFolderCommand }) =>
      apiClient.updateFolder(id, command),
    onSuccess: (folder) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.byProject(folder.projectId) });
      if (folder.parentFolderId !== null && folder.parentFolderId !== undefined) {
        queryClient.invalidateQueries({ queryKey: folderKeys.contents(folder.parentFolderId) });
      }
      toast.success('Folder renamed');
    },
    onError: () => {
      toast.error('Failed to rename folder');
    },
  });
};

export const useDeleteFolderMutation = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; parentFolderId: number | null }) =>
      apiClient.deleteFolder(id),
    onSuccess: (_, { parentFolderId }) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.byProject(projectId) });
      // Invalidate total folder count
      queryClient.invalidateQueries({ queryKey: folderKeys.countByProject(projectId) });
      if (parentFolderId !== null) {
        queryClient.invalidateQueries({ queryKey: folderKeys.contents(parentFolderId) });
      }
      // A deleted folder may have contained files, so the credit estimate can change.
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      invalidateActivityLogDelayed(queryClient, projectId);
      toast.success('Folder deleted');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error && error.message.toLowerCase().includes('child')
          ? 'Cannot delete a folder that contains subfolders. Delete the subfolders first.'
          : error instanceof Error && error.message.toLowerCase().includes('file')
          ? 'Cannot delete a folder that contains files. Delete the files first.'
          : 'Failed to delete folder';
      toast.error(message);
    },
  });
};

export const useUploadFileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, file }: { folderId: number; projectId: number; file: File }) =>
      apiClient.uploadFile(folderId, file),
    onSuccess: (fileItem, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.contents(fileItem.folderId) });
      // New file changes the token-cost estimate — refresh the project detail cache.
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      invalidateActivityLogDelayed(queryClient, projectId);
      if (fileItem.redactionWarning) {
        toast.warning(`"${fileItem.originalFileName}" uploaded — ${fileItem.redactionWarning}`);
      } else {
        toast.success(`"${fileItem.originalFileName}" uploaded`);
      }
    },
    onError: () => {
      toast.error('Failed to upload file');
    },
  });
};

export const useDeleteFileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, fileId }: { folderId: number; fileId: number; projectId: number }) =>
      apiClient.deleteFile(folderId, fileId),
    onSuccess: (_, { folderId, projectId }) => {
      queryClient.invalidateQueries({ queryKey: folderKeys.contents(folderId) });
      // Removing a file changes the token-cost estimate — refresh the project detail cache.
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      invalidateActivityLogDelayed(queryClient, projectId);
      toast.success('File deleted');
    },
    onError: () => {
      toast.error('Failed to delete file');
    },
  });
};
